import React, { useEffect, useState, useCallback, useRef } from "react";
import { apiGet } from "../../utils/api";
import { socket } from "../../socket";
import { FaSync, FaFilter, FaTerminal, FaCircle } from "react-icons/fa";

const EVENT_ICONS = {
  connected: { color: "text-green-400", symbol: "●" },
  disconnected: { color: "text-yellow-400", symbol: "●" },
  logged_out: { color: "text-red-400", symbol: "✖" },
  qr_generated: { color: "text-blue-400", symbol: "◎" },
  qr_expired: { color: "text-orange-400", symbol: "⏰" },
  error: { color: "text-red-500", symbol: "✖" },
  campaign_aborted: { color: "text-red-400", symbol: "⊘" },
  reconnecting: { color: "text-yellow-300", symbol: "↻" },
};

function formatTime(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export default function AdminSessionLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [live, setLive] = useState(true);
  const [filters, setFilters] = useState({ sessionId: "", email: "", event: "" });
  const termRef = useRef(null);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ limit: 200 });
      if (filters.sessionId) params.set("sessionId", filters.sessionId);
      if (filters.email) params.set("email", filters.email);
      if (filters.event) params.set("event", filters.event);

      const res = await apiGet(`/api/admin/session-logs?${params}`);
      if (res?.success) {
        setLogs(res.logs || []);
        setTotal(res.total || 0);
      } else {
        setError(res?.message || "Failed to fetch logs");
        setLogs([]);
      }
    } catch (err) {
      setError(err.message || "Network error");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Real-time refresh
  useEffect(() => {
    if (!live) return;
    const refresh = () => fetchLogs();
    socket.on("session_update", refresh);
    // Also poll every 30s as fallback
    const interval = setInterval(refresh, 30000);
    return () => {
      socket.off("session_update", refresh);
      clearInterval(interval);
    };
  }, [fetchLogs, live]);

  // Auto-scroll to top on new logs
  useEffect(() => {
    if (termRef.current && live) {
      termRef.current.scrollTop = 0;
    }
  }, [logs, live]);

  const clearFilters = () => setFilters({ sessionId: "", email: "", event: "" });
  const hasFilters = filters.sessionId || filters.email || filters.event;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-lg font-bold text-ink-primary flex items-center gap-2">
            <FaTerminal size={16} className="text-green-500" />
            Session Logs
          </h1>
          <p className="text-xs text-ink-secondary mt-0.5">
            Real-time session event stream ({total} total events)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setLive(l => !l)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
              live
                ? "bg-green-50 border-green-300 text-green-700"
                : "bg-surface-bg border-surface-border text-ink-secondary"
            }`}
          >
            <FaCircle size={6} className={live ? "text-green-500 animate-pulse" : "text-gray-400"} />
            {live ? "LIVE" : "Paused"}
          </button>
          <button
            onClick={fetchLogs}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-surface-border text-ink-secondary hover:text-ink-primary hover:bg-surface-bg transition-colors"
          >
            <FaSync size={10} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <input
          type="text"
          placeholder="Filter by session ID..."
          value={filters.sessionId}
          onChange={(e) => setFilters(f => ({ ...f, sessionId: e.target.value }))}
          className="px-2.5 py-1.5 text-xs rounded border border-surface-border bg-surface-bg text-ink-primary focus:outline-none focus:ring-1 focus:ring-brand-500 w-40 font-mono"
        />
        <input
          type="text"
          placeholder="Filter by email..."
          value={filters.email}
          onChange={(e) => setFilters(f => ({ ...f, email: e.target.value }))}
          className="px-2.5 py-1.5 text-xs rounded border border-surface-border bg-surface-bg text-ink-primary focus:outline-none focus:ring-1 focus:ring-brand-500 w-44"
        />
        <select
          value={filters.event}
          onChange={(e) => setFilters(f => ({ ...f, event: e.target.value }))}
          className="px-2.5 py-1.5 text-xs rounded border border-surface-border bg-surface-bg text-ink-primary focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="">All events</option>
          <option value="connected">Connected</option>
          <option value="disconnected">Disconnected</option>
          <option value="logged_out">Logged Out</option>
          <option value="qr_expired">QR Expired</option>
          <option value="error">Error</option>
          <option value="campaign_aborted">Campaign Aborted</option>
        </select>
        <button
          onClick={fetchLogs}
          className="px-2.5 py-1.5 text-xs font-medium rounded bg-brand-600 text-white hover:bg-brand-700 transition-colors flex items-center gap-1"
        >
          <FaFilter size={8} /> Apply
        </button>
        {hasFilters && (
          <button onClick={clearFilters} className="text-xs text-ink-secondary hover:text-ink-primary underline">
            Clear
          </button>
        )}
      </div>

      {/* Terminal */}
      <div className="flex-1 min-h-0 rounded-lg border border-gray-700 bg-gray-900 overflow-hidden flex flex-col">
        {/* Terminal title bar */}
        <div className="flex items-center gap-2 px-3 py-2 bg-gray-800 border-b border-gray-700">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
          </div>
          <span className="text-xs text-gray-400 font-mono ml-2">walytic-session-logs</span>
          {live && (
            <span className="ml-auto text-[10px] text-green-400 font-mono flex items-center gap-1">
              <FaCircle size={5} className="animate-pulse" /> streaming
            </span>
          )}
        </div>

        {/* Log entries */}
        <div ref={termRef} className="flex-1 overflow-y-auto p-3 font-mono text-xs leading-relaxed" style={{ minHeight: 400 }}>
          {error && (
            <div className="text-red-400 mb-3">
              <span className="text-red-500">ERROR:</span> {error}
              <br />
              <span className="text-gray-500">The session_logs table may not exist yet. Events will appear after the next session connect/disconnect.</span>
            </div>
          )}

          {loading && logs.length === 0 && !error && (
            <div className="text-gray-500">
              <span className="animate-pulse">Loading session events...</span>
            </div>
          )}

          {!loading && logs.length === 0 && !error && (
            <div className="text-gray-500">
              <div className="text-gray-400 mb-2">$ tail -f /var/log/walytic/sessions.log</div>
              <div className="text-gray-600">Waiting for events... Logs will appear here when sessions connect, disconnect, or encounter errors.</div>
              <div className="text-gray-600 mt-1">Try connecting or disconnecting a WhatsApp session to see entries.</div>
            </div>
          )}

          {logs.map((log) => {
            const ev = EVENT_ICONS[log.event] || { color: "text-gray-400", symbol: "·" };
            return (
              <div key={log.id} className="flex gap-2 py-0.5 hover:bg-gray-800/50 rounded px-1 -mx-1 group">
                <span className="text-gray-600 shrink-0 w-16">{formatDate(log.createdAt)}</span>
                <span className="text-gray-500 shrink-0 w-16">{formatTime(log.createdAt)}</span>
                <span className={`shrink-0 w-4 text-center ${ev.color}`}>{ev.symbol}</span>
                <span className={`shrink-0 w-28 ${ev.color}`}>{log.event}</span>
                <span className="text-cyan-400 shrink-0 w-28 truncate" title={log.sessionId}>
                  {log.session?.number || log.sessionId?.slice(0, 12) || "?"}
                </span>
                <span className="text-purple-400 shrink-0 w-40 truncate" title={log.userId}>
                  {log.user?.email || log.userId?.slice(0, 12) || "?"}
                </span>
                <span className="text-gray-400 truncate flex-1" title={log.detail}>
                  {log.detail || ""}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
